/// Account-wide risk, and the same figures recomputed for a hypothetical change - the model behind
/// every `before -> after` row on Markets and Multiply.
///
/// Horizon's pool is symmetric and cross-collateralised: one health factor spans every reserve, so
/// supplying WBTC changes what you can borrow in ETH. That is why this takes the whole reserve set
/// and a set of deltas rather than being a method on one reserve.
///
/// **Two honest approximations, both inherited rather than introduced here:**
///
/// 1. Every supplied balance is counted as collateral. The pool tracks that per-user in a
///    `userConfig` bitmap the frontend does not read, so a balance the user has NOT enabled as
///    collateral is over-counted here. `MarketsSection` has always made this assumption; it is
///    surfaced now rather than hidden, and every consumer labels the result as an estimate.
/// 2. Prices are the current oracle prices for both sides of the comparison. A projection is
///    "what this action does at today's prices", never a forecast.
///
/// The authoritative health factor is the pool's own `healthFactor(user)`. See `projectHealthFactor`
/// below for how the two are reconciled instead of competing.

const BPS = 10_000

/// `deltas`: `{ [reserveKey]: { supply?: number, debt?: number } }` in UNDERLYING units, signed -
/// positive supplies or borrows, negative withdraws or repays.
export function accountRisk(reserves, deltas = {}) {
  let rawCollateralUsd = 0
  let ltvWeightedUsd = 0
  let liquidationWeightedUsd = 0
  let debtUsd = 0

  for (const r of reserves ?? []) {
    const supply = Math.max(0, (r.user?.supplyBalance ?? 0) + (deltas[r.key]?.supply ?? 0))
    const debt = Math.max(0, (r.user?.debt ?? 0) + (deltas[r.key]?.debt ?? 0))
    const collateralValue = supply * r.priceUsd

    rawCollateralUsd += collateralValue
    ltvWeightedUsd += (collateralValue * r.collateralFactorBps) / BPS
    liquidationWeightedUsd += (collateralValue * r.liquidationThresholdBps) / BPS
    debtUsd += debt * r.priceUsd
  }

  // Null rather than Infinity when there is no debt: "no debt" is a different statement from "a
  // very large ratio", and rendering it as ∞ invites a reader to treat it as a number.
  const healthFactor = debtUsd > 0 ? liquidationWeightedUsd / debtUsd : null

  return {
    rawCollateralUsd,
    ltvWeightedUsd,
    liquidationWeightedUsd,
    debtUsd,
    healthFactor,
    availableBorrowsUsd: Math.max(0, ltvWeightedUsd - debtUsd),
    dropToLiquidationPct: dropToLiquidation(liquidationWeightedUsd, debtUsd),
  }
}

/// How far collateral can fall before the position is liquidated, as a percentage.
///
/// **The single most useful number on a borrow form.** A health factor of 1.06 means little to
/// most people; "your collateral falls 6% and you are liquidated" is the same fact expressed as a
/// decision someone can act on.
///
/// Liquidation is when `collateral x threshold == debt`. Scaling collateral by (1 - x) and solving
/// gives `x = 1 - debt / (collateral x threshold)`, which is `1 - 1/HF` as a price move.
///
/// Null when there is no debt (nothing to be liquidated); zero when already liquidatable.
export function dropToLiquidation(liquidationWeightedUsd, debtUsd) {
  if (debtUsd <= 0) return null
  if (liquidationWeightedUsd <= debtUsd) return 0
  return (1 - debtUsd / liquidationWeightedUsd) * 100
}

/// The projected health factor, ANCHORED to the pool's own answer.
///
/// The local model above and the contract can disagree slightly - the collateral-bit approximation,
/// rounding, and interest accrued since the last poll all move it. Showing a locally-computed
/// "before" beside `HealthPanel`'s on-chain figure would put two different health factors on one
/// page, which reads as a bug whichever one is right.
///
/// So the on-chain value is used as-is for "before", and the local model supplies only the RATIO
/// the action changes it by. The absolute number stays the contract's; the model is trusted only
/// for the relative move, which is the part it is actually good at.
export function projectHealthFactor(onChainHealthFactor, before, after) {
  if (after.healthFactor == null) return null           // the action clears all debt
  if (before.healthFactor == null || before.healthFactor === 0) return after.healthFactor
  if (onChainHealthFactor == null) return after.healthFactor
  return onChainHealthFactor * (after.healthFactor / before.healthFactor)
}

/// Zone thresholds, shared so "caution" and "danger" mean one thing everywhere they are rendered.
export function healthZone(healthFactor) {
  if (healthFactor == null) return 'safe'
  if (healthFactor >= 1.3) return 'safe'
  if (healthFactor >= 1.05) return 'caution'
  return 'danger'
}
